import {EventEmitter, EventSubscriber, HasEventName} from "@/modules/base/events";
import {inject, injectable} from "inversify";
import {CupCompetition, DivisionPromotion, KODivision, Matchday} from "@/modules/competitions/entity";
import {AfterSeasonEvaluationEvent} from "@/modules/season/events/AfterSeasonEvaluationEvent";
import {BankCategory} from "@/modules/finances/enums";
import {Match} from "@/modules/match/entity";
import {BeforeCupPromotionEvent} from "@/modules/competitions/events/BeforeCupPromotionEvent";
import {AfterCupPromotionEvent} from "@/modules/competitions/events/AfterCupPromotionEvent";
import {GateTypeEnum} from "@/modules/action-points/enums";
import GateManager from "@/modules/action-points/services/GateManager";

@injectable()
export class CupDivisionPromotionEventSubscriber implements EventSubscriber {
  constructor(
    @inject(EventEmitter) protected em: EventEmitter,
    @inject(GateManager) private gateManager: GateManager,
  ) {}

  priority(): number {
    return 0;
  }

  init(): { event: HasEventName; callback: (event: any) => void }[] {
    return [
      {event: AfterSeasonEvaluationEvent, callback: this.afterSeasonEvaluationEvent},
    ];
  }

  private isFirstDivision(competition: CupCompetition, division: KODivision): boolean {
    return (competition.divisions[0].id ?? null) === division.id;
  }

  async afterSeasonEvaluationEvent(event: AfterSeasonEvaluationEvent): Promise<void> {
    console.time('CupDivisionPromotionEventSubscriber::afterSeasonEvaluationEvent');

    const divisions = await KODivision
      .createQueryBuilder('division')
      .innerJoinAndSelect('division.promotions', 'promotion')
      .innerJoinAndSelect('division.competition', 'competition')
      .innerJoinAndSelect('division.matchdays', 'matchday')
      .innerJoinAndSelect('matchday.matches', 'match')
      .innerJoinAndSelect('match.advancedTeam', 'advancedTeam')
      .innerJoinAndSelect('competition.divisions', 'competitionDivision')
      .andWhere('promotion.endOfTheSeason = true')
      .getMany();

    await divisions
      .filter(d => d.competition instanceof CupCompetition)
      .forEachAsync(async (d: KODivision) => {
        await d.promotions.forEachAsync(async (p: DivisionPromotion) => {

          await d.matchdays.forEachAsync(async (matchday: Matchday) => {
            await matchday.matches.forEachAsync(async (match: Match) => {
              if (!match.advancedTeam) {
                return;
              }

              await this.em.emitAsync(new BeforeCupPromotionEvent(match.advancedTeam, d, p));

              if (p.price > 0 && match.advancedTeam.user) {
                await this.gateManager.getGate(GateTypeEnum.BALANCE).currency.add(
                  p.price,
                  {
                    category: BankCategory.REWARDS,
                    label: translator.t('reward_for_division_name', {name: d.name}),
                  }
                );
              }

              if (this.isFirstDivision(d.competition as CupCompetition, d)) {
                d.competition.winner = match.advancedTeam;
                await d.competition.save();
              }

              this.em.stack('cup-promotion', new AfterCupPromotionEvent(match.advancedTeam, d, p));
            });
          });
        });
      });

    await this.em.flushAsync('cup-promotion');

    console.timeEnd('CupDivisionPromotionEventSubscriber::afterSeasonEvaluationEvent');
  }
}